import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Observable, Subscription, map, catchError } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { WEIGHT_OPTIONS, ORDER_NUMBER_OPTIONS, PRODUCT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { BatchesService } from '../../services/batches.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { Batch, FieldOfBatch } from '../../models/batch';
import { ARTICLE, CUSTOMER, GROSS } from '../../../shared/models/settings';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

const sortWay = OrderByDirection.ASC;
const sortField = 'batchReference';

@Component({
	selector: 'copo-search-batch-list',
	templateUrl: './search-batch-list.component.html',
	animations: pageTransitions,
	styleUrl: './search-batch-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		VirtualScrollerDirective,
		CdkScrollable,
		TranslateModule,
		NgFor,
		MatRippleModule,
		RouterLink,
		NgIf,
		MatIconModule,
		LoadingSpinnerComponent,
		NumberPipe,
		DimensionPipe
	]
})
export class SearchBatchListComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	productDisplay: keyof typeof PRODUCT_OPTIONS = ARTICLE;
	orderDisplay: keyof typeof ORDER_NUMBER_OPTIONS = CUSTOMER;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	filter?: string | undefined;

	readonly UNDERSCORE = UNDERSCORE;

	batchRequiredFields: FieldOfBatch[] = [
		'id',
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'thickness',
		'width',
		'length',
		'cddWeek',
		'statusGroup',
		'statusGroupDescription',
		'status',
		'statusDescription',
		'currentLocation',
		'currentLocationDescription',
		'batchReference'
	];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly batchService: BatchesService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute
	) {
		this.user = this.userService.user;
		this.batchRequiredFields.push(
			'qtyBatchGross',
			'customerOrderNumber',
			'customerOrderItemNumber',
			'steelgradeDescription'
		);

		if (!this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			return;
		}
		this.searchTextSubscription = this.activedRoute.params.subscribe(p => {
			const searchText = <string | undefined>p['query'];
			this.filter = searchText ? this.batchService.createLuceneFilter(searchText) : undefined;
		});
	}

	get dataId() {
		const dataId = 'search-batch-list';
		return this.filter === undefined ? dataId : `${ dataId }-${ this.filter }`;
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Batch[]>) => void) {
		provideData(
			this.batchService.getBatches(
				this.batchRequiredFields,
				this.filter,
				index,
				limit,
				new OrderBy(sortField, sortWay),
				false
			).pipe(
				map(res => res.results),
				catchError((err: unknown) => {
					console.error('error retrieving batches', err);
					return [];
				})
			)
		);
	}

	getBatchWeight(batch: Batch) {
		if (this.weightDisplay === 'net') {
			return batch.qtyBatchNet;
		}
		return batch.qtyBatchGross;
	}

	getProductName(batch: Batch) {
		return batch.steelgradeDescription || UNDERSCORE;
	}

	getProductLabel() {
		return 'order.steelgrade';
	}

	getOrderValue(batch: Batch) {
		return batch.customerOrderNumber === ''
			? UNDERSCORE
			: `${ batch.customerOrderNumber }/${ batch.customerOrderItemNumber }`;
	}

	getOrderName() {
		return 'order.customer.order.number';
	}

	getIconStatus(batch: Batch) {
		switch (batch.statusGroup) {
		case '0':
			return 'in_production';
		case '1':
			return '';
		case '2':
			return 'in_stock';
		case '3':
			return 'in_transit';
		case '4':
			return '';
		case '5':
			return 'delivered';
		}
		return '';
	}

	getBatchStatusLocation(batch: Batch) {
		let statusLocation = batch.statusDescription;
		if (batch.currentLocationDescription) {
			statusLocation = `${ statusLocation } - ${ batch.currentLocationDescription }`;
		}
		return statusLocation;
	}
}
